export const BRAND_SHADE_STOPS = [200, 300, 400, 500, 600] as const;

export const NEUTRAL_SHADE_STOPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

/** The shade the user enters; the others are generated from it. */
export const BRAND_BASE_STOP = 500;

export const NEUTRAL_BASE_STOP = 900;

export type BrandShadeStop = (typeof BRAND_SHADE_STOPS)[number];

export type NeutralShadeStop = (typeof NEUTRAL_SHADE_STOPS)[number];

export const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

/** HSL lightness, in percent, of Tailwind's `neutral` palette — the scale Figma draws for Neutral. */
const NEUTRAL_REFERENCE_LIGHTNESS: Record<NeutralShadeStop, number> = {
  50: 98,
  100: 96.1,
  200: 89.8,
  300: 83.1,
  400: 63.9,
  500: 45.1,
  600: 32.2,
  700: 25.1,
  800: 14.9,
  900: 9,
  950: 3.9,
};

type Hsl = { h: number; s: number; l: number };

export const isHexColor = (value: string | undefined): value is string =>
  !!value && HEX_COLOR_PATTERN.test(value);

const hexToHsl = (hex: string): Hsl => {
  const [r, g, b] = [1, 3, 5].map((start) => Number.parseInt(hex.slice(start, start + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const delta = max - min;

  if (delta === 0) {
    return { h: 0, s: 0, l: l * 100 };
  }

  const s = delta / (1 - Math.abs(2 * l - 1));
  const sector = max === r ? ((g - b) / delta) % 6 : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4;

  return { h: (sector * 60 + 360) % 360, s: s * 100, l: l * 100 };
};

const hslToHex = ({ h, s, l }: Hsl) => {
  const saturation = s / 100;
  const lightness = Math.min(100, Math.max(0, l)) / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;

  const channel = (offset: number) => {
    const k = (offset + h / 30) % 12;
    const value = lightness - chroma / 2 * Math.max(-1, Math.min(k - 3, 9 - k, 1));

    return Math.round(value * 255)
      .toString(16)
      .padStart(2, "0");
  };

  return `#${channel(0)}${channel(8)}${channel(4)}`.toUpperCase();
};

/**
 * The entered colour is the 500 shade. Hue and saturation are kept and only lightness moves: 600 is
 * 500's lightness ÷ 1.25, and the lighter stops climb from 500 in equal steps towards white at 50.
 * This reproduces the sample brand palettes in the Theme Configurations v2.0 Figma to within 2%.
 */
export const generateBrandShades = (baseHex: string): Record<BrandShadeStop, string> => {
  const base = hexToHsl(baseHex);
  const stepToWhite = (100 - base.l) / 5;
  const withLightness = (l: number) => hslToHex({ ...base, l });

  return {
    200: withLightness(base.l + 3 * stepToWhite),
    300: withLightness(base.l + 2 * stepToWhite),
    400: withLightness(base.l + stepToWhite),
    [BRAND_BASE_STOP]: baseHex.toUpperCase(),
    600: withLightness(base.l / 1.25),
  };
};

/**
 * The entered colour is the 900 shade. Tailwind's `neutral` lightness curve is stretched so 900 lands
 * on the entered lightness while white stays white; hue and saturation are kept, so a tinted black
 * yields tinted greys.
 */
export const generateNeutralShades = (baseHex: string): Record<NeutralShadeStop, string> => {
  const base = hexToHsl(baseHex);
  const stretch = (100 - base.l) / (100 - NEUTRAL_REFERENCE_LIGHTNESS[NEUTRAL_BASE_STOP]);

  return Object.fromEntries(
    NEUTRAL_SHADE_STOPS.map((stop) => [
      stop,
      stop === NEUTRAL_BASE_STOP
        ? baseHex.toUpperCase()
        : hslToHex({ ...base, l: 100 - (100 - NEUTRAL_REFERENCE_LIGHTNESS[stop]) * stretch }),
    ]),
  ) as Record<NeutralShadeStop, string>;
};
